import * as React from "react";

// Third Party
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueries, useMutation, useQueryClient } from "@tanstack/react-query";

// helpers
import { useAccessPermission } from "@/helpers/acessPermission";
import { useToast } from "@/components/ui/use-toast";
import calculateDaysFromNow from "@/utils/calculateDaysFromNow";
import { RequireEditDocs } from "@/services/require-edit-docs";

// UI
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import { Button } from "@/components/ui/button";
import { PencilLine, ArrowLeft } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import NumberFormatSelector from "@/components/NumberFormatSelector";
import FileHandleViewer from "@/components/FileHandleViewer";
import { ModalRequireEditDoc } from "@/components/ModalRequireEditDoc";

// Services
import { getCash } from "@/services/finance";
import { CashDetail } from "@/types/finance";

export const Route = createFileRoute(
  "/_layout-private/report/finance/company/_layout/cash-flow-direct-method/"
)({
  component: RouteComponent, // Use App instead of RouteComponent
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const companyId = localStorage.getItem("companyId");
  const year = Number(localStorage.getItem("year"));
  const [open, setOpen] = React.useState(false);
  const theType = "DIRECT";
  const { company_financial_report } = useAccessPermission();

  React.useEffect(() => {
    if (!year) {
      navigate({
        to: "/report/finance/company/cash-flow-direct-method/add",
      });
    }
  }, [year, navigate]);

  const queries = useQueries({
    queries: [
      {
        queryKey: ["getCashDIRECT", companyId, year],
        queryFn: () => getCash(companyId as string, year, theType),
        enabled: !!companyId && !!year,
      },
      {
        queryKey: ["getCashDIRECT", companyId, year - 1],
        queryFn: () => getCash(companyId as string, year - 1, theType),
        enabled: !!companyId && !!year,
      },
    ],
  });

  const [
    { data, isLoading, error },
    { data: lastdata, isLoading: lastisLoading },
  ] = queries;

  React.useEffect(() => {
    if (!data?.data && !isLoading) {
      navigate({
        to: "/report/finance/company/cash-flow-direct-method/add",
      });
    }
  }, [data, navigate, isLoading]);
  const [unit, setUnit] = React.useState("1");

  const requireEditMutation = useMutation({
    mutationFn: (data: {
      editId: string;
      editRequestType: string;
      pendingReason: string;
      documentFile: string;
      editRequestStatus: string;
    }) => RequireEditDocs(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getCashDIRECT", companyId, year],
      });
      toast({
        title: "ສຳເລັດ",
        description: "ທ່ານໄດ້ສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານແລ້ວ",
        duration: 5000,
        variant: "success",
      });
      setOpen(false);
    },
    onError: () => {
      toast({
        title: "ຜິດພາດ",
        description:
          "ການສົ່ງຄໍາຮ້ອງຂໍແກ້ໄຂເອກະສານ ບໍ່ສາມາດເຮັດໄດ້ ກະລຸນາລອງໃໝ່ ຫຼື ຕິດຕໍ່ ຜູ້ໃຊ້ ຖ້າ ວ່າ ຍັງ ມີ ບັນ ຫາ",
        duration: 5000,
        variant: "destructive",
      });
    },
  });

  const dataLoading = isLoading || lastisLoading;

  if (dataLoading) {
    return <DinamixSkeletonTable />;
  }
  if (error) return <div>Error loading data</div>;

  const cashData = data?.data;

  const daysFromNow = calculateDaysFromNow(cashData?.updatedAt);

  const getSectionTitle = (type: string) => {
    const titles: Record<string, string> = {
      OPERATING_A: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
      INVESTING_B: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການລົງທຶນ",
      FINANCING_C: "ກະແສເງິນສົດທີ່ຕິດພັນກັບການປະກອບທຶນ",
      CASH_YEARLY: "ກະແສເງິນສົດລວມທີ່ຕິດພັນກັບການທຸລະກິດ",
      // ANNUAL_NET: "ກຳໄລປະຈຳປີ",
    };
    return titles[type];
  };

  const thisYearDetails = [
    {
      type: "OPERATING_A",
      data: data?.data?.cashDetails?.filter(
        (detail: any) => detail.cashDetailType === "OPERATING_A"
      ),
    },
    {
      type: "INVESTING_B",
      data: data?.data?.cashDetails?.filter(
        (detail: any) => detail.cashDetailType === "INVESTING_B"
      ),
    },
    {
      type: "FINANCING_C",
      data: data?.data?.cashDetails?.filter(
        (detail: any) => detail.cashDetailType === "FINANCING_C"
      ),
    },
    {
      type: "CASH_YEARLY",
      data: data?.data?.cashDetails?.filter(
        (detail: any) => detail.cashDetailType === "CASH_YEARLY"
      ),
    },
  ];

  const lastYearDetails = [
    {
      type: "OPERATING_A",
      data: lastdata?.data?.cashDetails?.filter(
        (detail: any) => detail.cashDetailType === "OPERATING_A"
      ),
    },
    {
      type: "INVESTING_B",
      data: lastdata?.data?.cashDetails?.filter(
        (detail: any) => detail.cashDetailType === "INVESTING_B"
      ),
    },
    {
      type: "FINANCING_C",
      data: lastdata?.data?.cashDetails?.filter(
        (detail: any) => detail.cashDetailType === "FINANCING_C"
      ),
    },
    {
      type: "CASH_YEARLY",
      data: lastdata?.data?.cashDetails?.filter(
        (detail: any) => detail.cashDetailType === "CASH_YEARLY"
      ),
    },
  ];

  const formatNumberWithCommas = (
    value: number | string,
    type: "INCOME" | "EXPENSE" | undefined
  ) => {
    if (!value || value === "-") return "0.00";

    // Convert to string and remove commas
    const stringValue = String(value).replace(/,/g, "");

    // Get absolute value for display
    const numericValue = Math.abs(Number(stringValue.replace(/[()]/g, "")));

    // If the value is invalid (NaN), return formatted zero
    if (Number.isNaN(numericValue)) {
      return "0.00";
    }

    // Format with commas and 2 decimal places
    const formattedValue = numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Return with parentheses for EXPENSE type
    return type === "EXPENSE" ? `(${formattedValue})` : formattedValue;
  };

  const renderRows = (
    thisYearData: CashDetail[] | undefined,
    lastYearData: CashDetail[] | undefined
  ) => {
    const mergedDetails = thisYearData?.map((thisYearDetail, index) => {
      // Find corresponding last year data based on 'name'
      const lastYearDetail = lastYearData?.find(
        (last) => last.name === thisYearDetail.name
      );

      // If no matching last year data, set it as "-".
      const lastYearAmount = lastYearDetail ? lastYearDetail?.amount : "-";
      const thisYearAmount = thisYearDetail?.amount;

      return (
        <TableRow key={index}>
          <TableCell className="border">{thisYearDetail.name}</TableCell>
          <TableCell className="border text-end">
            {formatNumberWithCommas(
              typeof thisYearAmount === "number"
                ? thisYearAmount / Number(unit)
                : thisYearAmount,
              thisYearDetail?.cashDataType
            )}
          </TableCell>
          <TableCell className="border text-end">
            {formatNumberWithCommas(
              typeof lastYearAmount === "number"
                ? lastYearAmount / Number(unit)
                : lastYearAmount,
              lastYearDetail?.cashDataType
            )}
          </TableCell>
          <TableCell className="border">{thisYearDetail.remark}</TableCell>
        </TableRow>
      );
    });
    return mergedDetails;
  };



  const handleRequireEdit = ({
    reason,
    file,
  }: {
    reason: string;
    file: string;
  }) => {
    if (!data?.data?.id) return;
    requireEditMutation.mutate({
      editId: data?.data?.id,
      editRequestType: "DIRECT_CASH_FLOW_REPORT",
      pendingReason: reason,
      documentFile: file,
      editRequestStatus: "REQUEST",
    });
  };

  return (
    <section>
      <Card className="border-none">
        <CardHeader className="border-b mb-[32px]">
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/finance",
                search: {
                  page: 1,
                  startYear: 0,
                  endYear: 0,
                },
              })
            }
            className="flex items-center gap-[4px] mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>

          <CardTitle>
            ລາຍລະອຽດເອກະສານທາງການເງິນງວດປີ {cashData?.yearly}
          </CardTitle>
          <CardDescription>ລາຍລະອຽດ</CardDescription>

          <div className="flex justify-end w-full mt-5 mb-5 gap-x-2">
            {daysFromNow <= 3 ? (

              <Button
                onClick={() =>
                  navigate({
                    to: "/report/finance/company/cash-flow-direct-method/edit",
                  })
                }
                variant="secondary"
                className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
              >
                <PencilLine /> ແກ້ໄຂ
              </Button>

            ) : (
              cashData?.editRequestStatus === "REQUEST" ? (
                <p className="text-red-500">ກະລຸນາລໍຖ້າການກວດສອບ</p>
              ) : (
                <Button
                  onClick={() => setOpen(true)}
                  variant="secondary"
                  className="flex items-center border border-blue-500 bg-none gap-x-1 w-[120px] rounded-md p-2 text-blue-500"
                >
                  <PencilLine /> ຂໍແກ້ໄຂເອກະສານ
                </Button>
              ))}



          </div>
          <ModalRequireEditDoc
            isOpen={open}
            onClose={() => setOpen(false)}
            onConfirm={handleRequireEdit}
            isLoading={requireEditMutation.isPending}
          />
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <NumberFormatSelector unit={unit} setUnit={setUnit} />
          </div>

          <Table className="mb-4">
            <TableHeader>
              <TableRow className="[&>*]:font-bold	[&>*]:text-black [&>*]:whitespace-nowrap">
                <TableHead className="border">ເນຶ້ອໃນລາຍງານ</TableHead>
                <TableHead className="border">ມູນຄ່າຍັງເຫຼືອ(ປີນີ້)</TableHead>
                <TableHead className="border">ມູນຄ່າຍັງເຫຼືອ(ປີກ່ອນ)</TableHead>
                <TableHead className="border">ໝາຍເຫດ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Map over each section (OPERATING_A, INVESTING_B, etc.) */}
              {thisYearDetails.map((section, index) => (
                <React.Fragment key={section.type}>
                  <TableRow className="bg-[#216C4C]/20 font-bold">
                    <TableCell colSpan={4}>
                      {getSectionTitle(section.type)}
                    </TableCell>
                  </TableRow>
                  {renderRows(section.data, lastYearDetails[index].data)}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>

          {cashData?.docFile
            ? company_financial_report.isDownload && (
              <FileHandleViewer fileUrl={cashData?.docFile} />
            )
            : null}
        </CardContent>
      </Card>
    </section>
  );
}
